import React, { useState } from "react";
import { 
  TableRow, 
  TableCell, 
  Box, 
  TextField, 
  Typography,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from "@mui/material";
import { 
  Add, 
  Delete, 
  CloudUpload 
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import ServiceGallery from "./ServiceGallery";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ServicesRow = ({ category, editRowId, editData, setUploadedImages, onAddService, onUploadImage, uploadedImages, onRemoveImageId, handleDeleteservice, setEditData }) => {
  const isEditing = editRowId === category.id;
  const [uploadingImages, setUploadingImages] = useState({});
  const [showAddService, setShowAddService] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [newService, setNewService] = useState({
    service_name: "",
    description: "", // CHANGED: service_description → description
    uploadedImageIds: [],
  });

  // Get current service data
  const getServiceData = (categoryId, serviceId) => {
    const categoryData = editData[categoryId];
    if (!categoryData || !categoryData.services) {
      return { service_name: "", description: "" }; // CHANGED: service_description → description
    }
    return categoryData.services[serviceId] || { service_name: "", description: "" }; // CHANGED: service_description → description
  };

  // Handle input change in edit mode - FIXED field names
  const handleServiceFieldChange = (categoryId, serviceId, field, value) => {
    setEditData(prev => {
      const updated = { ...prev };
      
      // Initialize category if it doesn't exist
      if (!updated[categoryId]) {
        updated[categoryId] = { services: {} };
      }
      
      // Initialize service if it doesn't exist
      if (!updated[categoryId].services[serviceId]) {
        updated[categoryId].services[serviceId] = {};
      }
      
      // Update the field
      updated[categoryId].services[serviceId][field] = value;
      
      return updated;
    });
  };

  // Handle image upload for existing services
  const handleExistingServiceImageUpload = async (event, categoryId, serviceId) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingImages(prev => ({
      ...prev,
      [serviceId]: true
    }));

    try {
      const successImages = [];
      
      for (const file of files) {
        try {
          const result = await onUploadImage(file, `Service image for ${newService.service_name || 'new service'}`);
          if (result.success) {
            successImages.push(result.image);
          }
        } catch (error) {
          console.error("Upload failed for file:", file.name, error);
        }
      }

      if (successImages.length > 0) {
        setEditData((prev) => {
          const updated = { ...prev };
          const categoryData = updated[categoryId] || { services: {} };
          const serviceData = categoryData.services?.[serviceId] || {};

          return {
            ...updated,
            [categoryId]: {
              ...categoryData,
              services: {
                ...categoryData.services,
                [serviceId]: {
                  ...serviceData,
                  gallery: [...(serviceData.gallery || []), ...successImages],
                },
              },
            },
          };
        });

        setUploadedImages(prev => ({
          ...prev,
          [serviceId]: [...(prev[serviceId] || []), ...successImages]
        }));

        setSnackbarMessage(`✅ Uploaded ${successImages.length} image(s)`);
      } else {
        setSnackbarMessage(`⚠️ Failed to upload images`);
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      setSnackbarMessage("Error uploading images");
    } finally {
      setUploadingImages(prev => ({
        ...prev,
        [serviceId]: false
      }));
      setShowSnackbar(true);
      event.target.value = '';
    }
  };

  // Handle image upload for NEW services
  const handleNewServiceImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setSnackbarMessage(`Uploading ${files.length} image(s)...`);
    setShowSnackbar(true);

    let successImages = [];

    for (const file of files) {
      try {
        const result = await onUploadImage(file);
        if (result.success) {
          successImages.push(result.image);
        }
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }

    if (successImages.length > 0) {
      setSnackbarMessage(`✅ Uploaded ${successImages.length} image(s)`);
      
      setNewService(prev => ({
        ...prev,
        uploadedImageIds: [...prev.uploadedImageIds, ...successImages.map(img => img.id)]
      }));
    } else {
      setSnackbarMessage(`⚠️ Failed to upload images`);
    }
  };

  // Handle add new service - FIXED field names
  const handleAddNewService = async () => {
    if (!newService.service_name.trim() || !newService.description.trim()) { // CHANGED: service_description → description
      setSnackbarMessage("Please fill in all required fields");
      setShowSnackbar(true);
      return;
    }

    try {
      if (onAddService) {
        // Create service object with correct field names for API
        const serviceToAdd = {
          service_name: newService.service_name,
          description: newService.description, // CHANGED: service_description → description
          uploadedImageIds: newService.uploadedImageIds
        };

        await onAddService(category.id, serviceToAdd);
        setSnackbarMessage("Service added successfully!");
        setShowSnackbar(true);
        
        // Reset form and close dialog
        setNewService({
          service_name: "",
          description: "", // CHANGED: service_description → description
          uploadedImageIds: []
        });
        setShowAddService(false);
      }
    } catch (error) {
      setSnackbarMessage("Error adding service");
      setShowSnackbar(true);
    }
  };

  // Get uploaded images for display in new service dialog
  const getNewServiceUploadedImages = () => {
    if (Array.isArray(uploadedImages)) {
      return uploadedImages;
    }
    return [];
  };

  return (
    <>
      <TableRow>
        <TableCell colSpan={4} sx={{ border: "2px solid #e0e0e0", p: 2, m: 1, backgroundColor: '#fafafa' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Services for {category.name}
            </Typography>
            
            <Button 
              variant="contained"  
              startIcon={<Add />}
              sx={{
                backgroundColor: 'black',
                color: 'white',
                '&:hover': { backgroundColor: '#333' },
              }} 
              onClick={() => setShowAddService(true)}
            >
              Add Service
            </Button>
          </Box>
          
          {category.services.map(service => {
            const serviceData = getServiceData(category.id, service.id);
            
            return (
              <Box 
                key={service.id} 
                sx={{ 
                  display: "flex", 
                  flexDirection: { xs: "column", md: "row" }, 
                  gap: 2, 
                  mt: 2, 
                  p: 2, 
                  border: "1px solid #e0e0e0", 
                  borderRadius: 2,
                  backgroundColor: 'white'
                }}
              >
                {/* Service Name and Description */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  {isEditing ? (
                    <>
                      <TextField
                        label="Service Name"
                        value={serviceData.service_name || service.service_name || ""}
                        onChange={e => handleServiceFieldChange(category.id, service.id, "service_name", e.target.value)}
                        size="small"
                        fullWidth
                        variant="outlined"
                      />
                      <TextField
                        label="Description"
                        value={serviceData.description || service.description || ""} // CHANGED: service_description → description
                        onChange={e => handleServiceFieldChange(category.id, service.id, "description", e.target.value)} // CHANGED: service_description → description
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteservice(service.id, category.id)}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {service.service_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {service.description} {/* CHANGED: service_description → description */}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteservice(service.id, category.id)}
                        sx={{ mt: 1, alignSelf: "flex-start" }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Box>

                {/* Image Upload Section - Only in Edit Mode */}
                {isEditing && (
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Upload Images (Multiple)
                    </Typography>
                    
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      disabled={uploadingImages[service.id]}
                      sx={{ mb: 1 }}
                    >
                      {uploadingImages[service.id] ? "Uploading..." : "Add Images"}
                      <VisuallyHiddenInput 
                        type="file" 
                        multiple 
                        accept="image/*"
                        onChange={(e) => handleExistingServiceImageUpload(e, category.id, service.id)}
                      />
                    </Button>

                    {/* Uploaded Images Preview */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {serviceData.gallery?.map((image) => (
                        <Box
                          key={image.id}
                          sx={{
                            position: 'relative',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            overflow: 'hidden',
                            width: 80,
                            height: 80
                          }}
                        >
                          <img
                            src={image.url || image.previewUrl || image}
                            alt="Service"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              backgroundColor: 'rgba(255,255,255,0.8)',
                              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                            }}
                            onClick={() => onRemoveImageId(service.id, image.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Supported formats: JPG, PNG, WEBP • Max 10 images • Max 5MB per image
                    </Typography>
                  </Box>
                )}

                {/* Gallery Display - Only in View Mode */}
                {!isEditing && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Gallery
                    </Typography>
                    <ServiceGallery gallery={service.gallery || []} />
                  </Box>
                )}
              </Box>
            );
          })}

          {/* Empty State */}
          {category.services.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No services available for this category.
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<Add />}
                onClick={() => setShowAddService(true)}
                sx={{ mt: 2 }}
              >
                Add First Service
              </Button>
            </Box>
          )}

          {isEditing && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You can upload multiple images for each service. Click "Add Images" to select multiple files.
            </Alert>
          )}
        </TableCell>
      </TableRow>

      {/* Add Service Dialog - FIXED field names */}
      <Dialog 
        open={showAddService} 
        onClose={() => setShowAddService(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Service to {category.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Service Name *"
              value={newService.service_name}
              onChange={(e) => setNewService(prev => ({ ...prev, service_name: e.target.value }))}
              fullWidth
              variant="outlined"
              required
            />
            <TextField
              label="Service Description *"
              value={newService.description} // CHANGED: service_description → description
              onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))} // CHANGED: service_description → description
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              required
            />
            
            {/* Image Upload for New Service */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Service Images (Optional) - Multiple allowed
              </Typography>
              
              {getNewServiceUploadedImages().length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Uploaded Images ({getNewServiceUploadedImages().length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {getNewServiceUploadedImages().map((imageData, index) => (
                      <Box 
                        key={imageData.id} 
                        sx={{ 
                          position: 'relative',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          overflow: 'hidden',
                          width: 100,
                          height: 100
                        }}
                      >
                        <img
                          src={imageData.previewUrl}
                          alt={`Uploaded ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '2px 4px',
                            fontSize: '10px'
                          }}
                        >
                          ID: {imageData.id.substring(0, 8)}...
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                          }}
                          onClick={() => onRemoveImageId(null, imageData.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ mb: 1 }}
              >
                {getNewServiceUploadedImages().length > 0 ? 'Add More Images' : 'Upload Images'}
                <VisuallyHiddenInput 
                  type="file" 
                  multiple 
                  accept="image/*"
                  onChange={handleNewServiceImageUpload}
                />
              </Button>
              
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Supported formats: JPG, PNG, WEBP. No size limit. Multiple images allowed.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddService(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddNewService}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Add Service
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default ServicesRow;